import { computed, ref } from 'vue'
import gql from 'graphql-tag'
import { useQuery } from '@vue/apollo-composable'

import { type File, type Item, ItemSort } from '@/shared/graphql/router/graphql'
import { isFile, isFolder } from '@/features/explorer/utils/itemGuards'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { formatTimestamp } from '@/shared/utils/datetime'
import { closeDialog, triggerDialogEvent } from '@/shared/dialogs/dialog'
import type { BreadcrumbItem, TableSort } from '@dacr/library'

const PAGE_SIZE = 30

const ITEM_SORT_MAP: Record<string, ItemSort> = {
  'name:asc': ItemSort.NameAsc,
  'name:desc': ItemSort.NameDesc,
  'timestamp:asc': ItemSort.TimestampAsc,
  'timestamp:desc': ItemSort.TimestampDesc,
}

const SELECT_COPYRIGHT_WORK = gql`
  query SelectCopyrightWork(
    $path: String!
    $first: Int
    $after: String
    $sort: ItemSort
  ) {
    folderByPath(path: $path) {
      id
      items(input: { first: $first, after: $after, sort: $sort }) {
        edges {
          cursor
          node {
            ... on File {
              id
              name
              type
              copyright {
                id
                registrations {
                  timestamp
                }
              }
              asset {
                previewImageUrl
              }
            }
            ... on Folder {
              id
              name
              path
              totalFiles
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
`

type WorkConnection = {
  edges: { cursor: string; node: Item }[]
  pageInfo: { endCursor: string | null; hasNextPage: boolean }
}

type WorkResult = {
  folderByPath: { id: string; items: WorkConnection | null } | null
}

type WorkVars = {
  path: string
  first?: number
  after?: string | null
  sort?: ItemSort
}

export const useSelectCopyright = () => {
  const path = ref<{ name: string; path: string }[]>([
    { name: 'Dacr', path: '/' },
  ])
  const folderPath = computed(() => path.value.at(-1)!.path)

  const sort = ref<TableSort | null>({ field: 'name', direction: 'asc' })
  const sortBy = computed<ItemSort>(
    () =>
      ITEM_SORT_MAP[`${sort.value?.field}:${sort.value?.direction}`] ??
      ItemSort.NameAsc
  )

  const { result, loading, fetchMore } = useQuery<WorkResult, WorkVars>(
    SELECT_COPYRIGHT_WORK,
    () => ({ path: folderPath.value, first: PAGE_SIZE, sort: sortBy.value }),
    { fetchPolicy: 'cache-and-network', clientId: 'router' }
  )

  const items = computed(
    () => result.value?.folderByPath?.items?.edges?.map((e) => e.node) ?? []
  )

  const pageInfo = computed(() => result.value?.folderByPath?.items?.pageInfo)
  const hasMore = computed(() => !!pageInfo.value?.hasNextPage)

  const loadingMore = ref(false)
  const loadMore = async () => {
    if (!hasMore.value || loadingMore.value || loading.value) return
    loadingMore.value = true
    try {
      await fetchMore({
        variables: { after: pageInfo.value?.endCursor },
        updateQuery: (prev, { fetchMoreResult }) => {
          const folder = fetchMoreResult?.folderByPath
          if (!folder?.items) return prev
          return {
            folderByPath: {
              ...folder,
              items: {
                ...folder.items,
                edges: [
                  ...(prev.folderByPath?.items?.edges ?? []),
                  ...folder.items.edges,
                ],
              },
            },
          }
        },
      })
    } finally {
      loadingMore.value = false
    }
  }

  const breadcrumbPath = computed(() => path.value.map((p) => p.name))
  const breadcrumbItems = computed<BreadcrumbItem[]>(() =>
    path.value.map((p) => ({ label: p.name }))
  )

  const thumbSrc = (item: File) =>
    applyAuthorizationToken(item.asset?.previewImageUrl, [
      { key: 'width', value: 72 },
    ]) ?? undefined

  const timestampLabel = (item: File) =>
    formatTimestamp(item.copyright?.registrations?.[0]?.timestamp) || '—'

  const moreCount = (item: File) =>
    Math.max((item.copyright?.registrations?.length ?? 0) - 1, 0)

  const handleBreadcrumbSelect = (_item: BreadcrumbItem, index: number) => {
    path.value = path.value.slice(0, index + 1)
  }

  const openFolder = (item: Item) => {
    if (isFolder(item)) path.value.push({ name: item.name, path: item.path })
  }

  const selected = ref<File>()

  const select = (item: File) => {
    selected.value = selected.value?.id === item.id ? undefined : item
  }

  const confirm = () => {
    if (!selected.value) return
    triggerDialogEvent('success', selected.value)
    closeDialog()
  }

  const handleItemClick = (item: Item) => {
    if (isFolder(item)) return openFolder(item)
    if (isFile(item)) {
      triggerDialogEvent('success', item)
      closeDialog()
    }
  }

  return {
    loading,
    loadingMore,
    hasMore,
    loadMore,
    sort,
    items,
    breadcrumbPath,
    breadcrumbItems,
    thumbSrc,
    timestampLabel,
    moreCount,
    handleBreadcrumbSelect,
    openFolder,
    selected,
    select,
    confirm,
    handleItemClick,
    isFile,
    isFolder,
  }
}
